/*
  Sisters Moto: Modern Header behaviour (moved verbatim from sections/header.liquid
  so it is cached between page views). Loaded with `defer`; the section keeps a tiny
  inline bootstrap that registers the same DOMContentLoaded / shopify:section:load
  listeners at the same point in the page as before, so init timing is unchanged
  (deferred scripts always run before DOMContentLoaded fires).

  Surcharge product id comes from window.smSurchargeId (set in layout/theme.liquid
  and sections/header.liquid from Theme settings > Overweight surcharge product).
*/
(function () {

if (window.ModernHeader) return; // theme editor can re-insert the section's script tags

class ModernHeader {
  constructor() {
    this.init();
  }

  init() {
    if (document.documentElement.classList.contains('header-initialized')) return;
    document.documentElement.classList.add('header-initialized');

    this.setupMobileMenu();
    this.setupSearchToggle(); /* NEW: expanding icon search open/close */
    this.setupSearch();
    this.setupMobileSearch();
    this.setupMobileDrawerSearch();
    this.setupCartUpdates();
    this.setupStickyHeader();
    this.setupRevealHeader();
    this.setupYmmMegaMenu();
    this.setupCategoryMegaMenu();
    this.setupBrandMegaMenu();
    this.setupBottomNav();
    this.setupMobileInteractions();
    this.setupShopDrawer();
    this.setupVehicleDrawer();
    this.setupBrandDrawer();
    this.setupTertiaryVehicleForm();
    this.setupDesktopMenuKeyboard();
  }

  /* ============================================================
     NEW: Expanding icon search (desktop)
     - Click icon to open, autofocus input
     - Close via X button, Esc key, or click outside
     - aria-expanded kept in sync
     ============================================================ */
  setupSearchToggle() {
    const toggle = document.getElementById('search-toggle');
    const panel = document.getElementById('search-container');
    const input = document.getElementById('main-search-input');
    const closeBtn = document.getElementById('search-close');
    const wrap = document.getElementById('header-search-wrap');
    const suggestions = document.getElementById('search-suggestions');

    if (!toggle || !panel || !input) return;

    const openSearch = () => {
      panel.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close search');
      window.requestAnimationFrame(() => input.focus());
    };

    const closeSearch = (returnFocus) => {
      panel.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open search');
      if (suggestions) suggestions.style.display = 'none';
      if (returnFocus) toggle.focus();
    };

    toggle.addEventListener('click', () => {
      panel.classList.contains('is-open') ? closeSearch(true) : openSearch();
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', () => closeSearch(true));
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) {
        closeSearch(true);
      }
    });

    document.addEventListener('click', (e) => {
      if (panel.classList.contains('is-open') && wrap && !wrap.contains(e.target)) {
        closeSearch(false);
      }
    });
  }

  setupMobileMenu() {
    const mobileToggle = document.getElementById('mobile-menu-toggle');
    const navMenu = document.getElementById('nav-menu');
    const body = document.body;
    let scrollPosition = 0;

    const openDrawer = () => {
      // Save current scroll position
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      body.classList.add('drawer-open');
      body.style.top = `-${scrollPosition}px`;
    };

    const closeDrawer = () => {
      // Restore scroll position
      body.classList.remove('drawer-open');
      body.style.top = '';
      window.scrollTo(0, scrollPosition);
    };

    if (mobileToggle && navMenu) {
      // a11y: expose drawer state, keep keyboard focus inside the open drawer and
      // hand it back to the hamburger when the drawer closes.
      mobileToggle.setAttribute('aria-controls', 'nav-menu');
      mobileToggle.setAttribute('aria-expanded', 'false');

      const syncToggle = (open) => {
        mobileToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        mobileToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      };

      const focusFirstInDrawer = () => {
        const first = navMenu.querySelector('.nav-item > .nav-link, .nav-item > a');
        if (first) window.requestAnimationFrame(() => first.focus({ preventScroll: true }));
      };

      // The drawer starts right under the header (its height changes with the announcement bar),
      // not at a fixed 60px where its top part sat hidden under the header.
      const placeDrawer = () => {
        const bar = mobileToggle.closest('header') || mobileToggle.parentElement;
        const top = bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 60;
        navMenu.style.setProperty('--sm-drawer-top', top + 'px');
      };

      // Phone drawer: category groups fold open on tap (sections/header.liquid .mega-acc-toggle)
      navMenu.addEventListener('click', (e) => {
        const toggle = e.target.closest('.mega-acc-toggle');
        if (toggle) {
          const column = toggle.closest('.mega-category-column');
          const open = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (column) column.classList.toggle('is-open', open);
          return;
        }
        // Quick links that open something else (the bike picker) close the drawer first
        if (e.target.closest('[data-sm-drawer-close]') && navMenu.classList.contains('active')) {
          navMenu.classList.remove('active');
          syncToggle(false);
          closeDrawer();
        }
      });

      mobileToggle.addEventListener('click', () => {
        if (!navMenu.classList.contains('active')) placeDrawer();
        const isActive = navMenu.classList.toggle('active');
        syncToggle(isActive);
        // Prevent body scroll when drawer is open
        if (isActive) {
          openDrawer();
          // Locking the page scroll can move the header (it leaves its sticky spot): measure again
          window.requestAnimationFrame(placeDrawer);
          focusFirstInDrawer();
        } else {
          closeDrawer();
        }
      });

      // Close menu when clicking outside
      document.addEventListener('click', (e) => {
        if (!navMenu.contains(e.target) && !mobileToggle.contains(e.target) && navMenu.classList.contains('active')) {
          navMenu.classList.remove('active');
          syncToggle(false);
          closeDrawer();
        }
      });

      // Close drawer on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && navMenu.classList.contains('active')) {
          // A drill-down panel handles its own Escape (see setupMobileInteractions)
          if (navMenu.querySelector('.mega-menu.active, .brand-mega-menu.active, .ymm-mega-menu.active')) return;
          navMenu.classList.remove('active');
          syncToggle(false);
          closeDrawer();
          mobileToggle.focus({ preventScroll: true });
        }
      });

      // Focus trap: Tab / Shift+Tab cycle through the hamburger + visible drawer controls
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab' || !navMenu.classList.contains('active')) return;
        const focusables = [mobileToggle].concat(Array.from(navMenu.querySelectorAll(
          'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ))).filter((el) => el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== 'hidden');
        if (focusables.length < 2) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const current = document.activeElement;
        if (!focusables.includes(current)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        } else if (e.shiftKey && current === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && current === last) {
          e.preventDefault();
          first.focus();
        }
      });
    }
  }

  /* ============================================
     YMM + SEARCH INTEGRATION
     When a vehicle is selected, keyword searches go to MotoFit's
     vehicle-filtered results instead of plain Shopify search, so the
     customer's stated vehicle is actually respected.

     Parameter format confirmed from MotoFit's own widget source
     (buildFilteredProductsPageUrl): /apps/get-api-data with
     type=filteredProducts plus year/make/model IDs and motofitSearchQuery.

     IMPORTANT: MotoFit filters on the numeric option IDs it stores in
     cookies (year/make/model), NOT the display labels. window.applied_filters
     holds those IDs; window.motofitSelectedVehicle holds the human-readable
     labels. We need the former here.
     ============================================ */
  buildSearchUrl(query) {
    var q = (query || '').trim();
    if (!q) return null;

    var shopifySearch = '/search?q=' + encodeURIComponent(q);

    var f = window.applied_filters;
    if (!f) return shopifySearch;

    var year = f.year, make = f.make, model = f.model;
    var hasVehicle = year && make && model &&
      String(year) !== 'null' && String(make) !== 'null' && String(model) !== 'null';

    if (!hasVehicle) return shopifySearch;

    var params = new URLSearchParams();
    params.set('type', 'filteredProducts');
    params.set('year', year);
    params.set('make', make);
    params.set('model', model);
    params.set('motofitSearchQuery', q);
    params.set('page', '1');
    return '/apps/get-api-data?' + params.toString();
  }

  goToSearch(query) {
    var url = this.buildSearchUrl(query);
    if (!url) return;
    this.saveRecentSearch(query);
    window.location.href = url;
  }

  /* ============================================
     SEARCH PANEL HELPERS
     - Recent searches: localStorage 'smoto:recent-searches' (newest first,
       max 5), saved on submit and when a suggestion is chosen.
     - Popular searches: header section setting "Popular searches"
       (window.smSearchConfig.popular, one per line).
     - Current bike: window.SmHomeBike (snippets/sm-home-bike.liquid,
       rendered by sections/header.liquid), same link as the garage.
     ============================================ */
  getRecentSearches() {
    try {
      var list = JSON.parse(window.localStorage.getItem('smoto:recent-searches') || '[]');
      if (!Array.isArray(list)) return [];
      return list.filter(function (t) { return typeof t === 'string' && t.trim(); }).slice(0, 5);
    } catch (e) {
      return [];
    }
  }

  setRecentSearches(list) {
    try {
      window.localStorage.setItem('smoto:recent-searches', JSON.stringify(list.slice(0, 5)));
    } catch (e) {}
  }

  saveRecentSearch(query) {
    var q = String(query || '').trim().replace(/\s+/g, ' ').slice(0, 80);
    if (!q) return;
    var lower = q.toLowerCase();
    var list = this.getRecentSearches().filter(function (t) { return t.toLowerCase() !== lower; });
    list.unshift(q);
    this.setRecentSearches(list);
  }

  removeRecentSearch(term) {
    var lower = String(term || '').toLowerCase();
    this.setRecentSearches(this.getRecentSearches().filter(function (t) { return t.toLowerCase() !== lower; }));
  }

  getPopularSearches() {
    var raw = (window.smSearchConfig && window.smSearchConfig.popular) || '';
    var seen = {};
    return String(raw).split(/\r?\n/).map(function (t) { return t.trim(); }).filter(function (t) {
      var k = t.toLowerCase();
      if (!t || seen[k]) return false;
      seen[k] = true;
      return true;
    }).slice(0, 8);
  }

  getCurrentBike() {
    try {
      var bike = window.SmHomeBike && typeof window.SmHomeBike.get === 'function' ? window.SmHomeBike.get() : null;
      return bike && bike.name && bike.url ? bike : null;
    } catch (e) {
      return null;
    }
  }

  escapeAttr(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // "Shop parts that fit your <bike> →" row (an option, so it is keyboard reachable)
  bikeRowHtml(namespace) {
    var bike = this.getCurrentBike();
    if (!bike) return '';
    return '<div class="sm-search-bike" id="' + namespace + '-sm-search-bike" role="option" aria-selected="false">' +
        '<a class="sm-search-bike__link" href="' + this.escapeAttr(bike.url) + '" tabindex="-1">' +
          '<span class="sm-search-bike__text">Shop parts that fit your <strong>' + this.escapeAttr(bike.name) + '</strong></span>' +
          '<span class="sm-search-bike__arrow" aria-hidden="true">&rarr;</span>' +
        '</a>' +
      '</div>';
  }

  // Shown when a search box is focused and empty
  emptyPanelHtml(namespace) {
    var self = this;
    var esc = function (v) { return self.escapeAttr(v); };
    var clockIcon = '<svg class="sm-search-option__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"></circle><polyline points="12 7 12 12 15 14"></polyline></svg>';
    var recent = this.getRecentSearches();
    var popular = this.getPopularSearches();
    var html = this.bikeRowHtml(namespace);

    if (recent.length) {
      html += '<div class="sm-search-group sm-search-group--recent">' +
        '<div class="sm-search-group__head">' +
          '<h2 class="sm-search-group__title" id="' + namespace + '-sm-recent-heading">Recent searches</h2>' +
          '<button type="button" class="sm-search-clear" data-sm-clear-recent>Clear<span class="visually-hidden"> recent searches</span></button>' +
        '</div>' +
        '<ul class="sm-search-list list-unstyled" role="group" aria-labelledby="' + namespace + '-sm-recent-heading">' +
        recent.map(function (t, i) {
          return '<li class="sm-search-recent" role="none">' +
              '<div class="sm-search-option sm-search-option--recent" id="' + namespace + '-sm-recent-' + i + '" role="option" aria-selected="false" data-sm-term="' + esc(t) + '">' +
                '<a href="' + esc(self.buildSearchUrl(t) || '/search') + '" tabindex="-1">' + clockIcon + '<span class="sm-search-option__label">' + esc(t) + '</span></a>' +
              '</div>' +
              '<button type="button" class="sm-search-remove" data-sm-remove-recent="' + esc(t) + '" aria-label="Remove ' + esc(t) + ' from recent searches"><span aria-hidden="true">&#10005;</span></button>' +
            '</li>';
        }).join('') +
        '</ul>' +
      '</div>';
    }

    if (popular.length) {
      html += '<div class="sm-search-group sm-search-group--popular">' +
        '<div class="sm-search-group__head">' +
          '<h2 class="sm-search-group__title" id="' + namespace + '-sm-popular-heading">Popular searches</h2>' +
        '</div>' +
        '<ul class="sm-search-chips list-unstyled" role="group" aria-labelledby="' + namespace + '-sm-popular-heading">' +
        popular.map(function (t, i) {
          return '<li class="sm-search-chip" id="' + namespace + '-sm-popular-' + i + '" role="option" aria-selected="false" data-sm-term="' + esc(t) + '">' +
              '<a href="' + esc(self.buildSearchUrl(t) || '/search') + '" tabindex="-1">' + esc(t) + '</a>' +
            '</li>';
        }).join('') +
        '</ul>' +
      '</div>';
    }

    return html ? '<div class="sm-search-panel">' + html + '</div>' : '';
  }

  /* ============================================
     ADVANCED SEARCH
     One shared implementation wired to three inputs
     (desktop bar, mobile modal, mobile drawer).
     Uses Shopify's predictive-search SECTION endpoint
     (not the bare .json endpoint) so results are real
     Liquid product objects and can show SKU / price /
     image / description matches, not just title+vendor.
     ============================================ */
  setupSearch() {
    this.initSearchInput('main-search-input', 'search-suggestions', 'desktop');

    // Intercept native form submits (magnifier button / Enter in some
    // browsers) so they route through the same YMM-aware logic. The form's
    // action attribute is left intact, so if this JS ever fails the form
    // still degrades to a normal Shopify search rather than breaking.
    document.querySelectorAll('form[data-ymm-aware-search]').forEach((form) => {
      form.addEventListener('submit', (e) => {
        var input = form.querySelector('input[name="q"]');
        if (!input) return;
        var url = this.buildSearchUrl(input.value);
        if (!url) return;
        e.preventDefault();
        this.saveRecentSearch(input.value);
        window.location.href = url;
      });
    });
  }

  setupMobileSearch() {
    const mobileToggle = document.getElementById('mobile-search-toggle');
    const mobileModal = document.getElementById('mobile-search-modal');
    const mobileClose = document.getElementById('mobile-search-close');
    const mobileInput = document.getElementById('mobile-search-input');

    if (mobileToggle && mobileModal) {
      mobileToggle.setAttribute('aria-expanded', 'false');

      // a11y: close returns focus to the trigger; Escape closes; Tab stays inside.
      const closeModal = (returnFocus) => {
        if (!mobileModal.classList.contains('active')) return;
        mobileModal.classList.remove('active');
        mobileToggle.setAttribute('aria-expanded', 'false');
        if (returnFocus) mobileToggle.focus({ preventScroll: true });
      };

      mobileToggle.addEventListener('click', () => {
        mobileModal.classList.add('active');
        mobileToggle.setAttribute('aria-expanded', 'true');
        setTimeout(() => mobileInput && mobileInput.focus(), 100);
      });

      mobileClose && mobileClose.addEventListener('click', () => {
        closeModal(true);
      });

      mobileModal.addEventListener('click', (e) => {
        if (e.target === mobileModal) {
          closeModal(true);
        }
      });

      mobileModal.addEventListener('keydown', (e) => {
        if (!mobileModal.classList.contains('active')) return;
        if (e.key === 'Escape') {
          closeModal(true);
          return;
        }
        if (e.key !== 'Tab') return;
        const focusables = Array.from(mobileModal.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled])'))
          .filter((el) => el.getClientRects().length > 0);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    }

    this.initSearchInput('mobile-search-input', 'mobile-search-suggestions', 'mobilemodal');
  }

  setupMobileDrawerSearch() {
    this.initSearchInput('mobile-drawer-search-input', 'mobile-drawer-search-suggestions', 'mobiledrawer');
  }

  initSearchInput(inputId, containerId, namespace) {
    const input = document.getElementById(inputId);
    const container = document.getElementById(containerId);
    if (!input || !container) return;

    let searchTimeout;
    let activeIndex = -1;
    let mode = ''; // '' (closed), 'panel' (empty-focus panel) or 'results'

    // Combobox + listbox semantics (the desktop markup already had most of these)
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', containerId);
    input.setAttribute('aria-expanded', 'false');
    container.setAttribute('role', 'listbox');
    if (!container.hasAttribute('aria-label')) container.setAttribute('aria-label', 'Search suggestions');

    const isOpen = () => container.style.display === 'block';

    const clearActive = () => {
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      container.querySelectorAll('[role="option"][aria-selected="true"]').forEach((el) => el.setAttribute('aria-selected', 'false'));
    };

    const closeResults = () => {
      clearTimeout(searchTimeout);
      // Drop any request still in flight so it can't re-open the dropdown
      this.__searchRequestId = (this.__searchRequestId || 0) + 1;
      container.style.display = 'none';
      container.innerHTML = '';
      mode = '';
      clearActive();
      input.setAttribute('aria-expanded', 'false');
    };

    // Visible options only, in DOM order (bike row, groups, "Search for" row)
    const getOptions = () => Array.from(container.querySelectorAll('[role="option"]'))
      .filter((el) => el.getClientRects().length > 0);

    const setActive = (index) => {
      const options = getOptions();
      if (!options.length) return;
      options.forEach((el) => el.setAttribute('aria-selected', 'false'));
      activeIndex = (index + options.length) % options.length;
      const active = options[activeIndex];
      if (!active.id) active.id = namespace + '-sm-option-' + activeIndex;
      active.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    };

    const showPanel = () => {
      if (input.value.trim()) return;
      const html = this.emptyPanelHtml(namespace);
      if (!html) {
        closeResults();
        return;
      }
      clearTimeout(searchTimeout);
      this.__searchRequestId = (this.__searchRequestId || 0) + 1;
      container.innerHTML = html;
      container.style.display = 'block';
      mode = 'panel';
      clearActive();
      input.setAttribute('aria-expanded', 'true');
    };

    // Enter / click on an option
    const activateOption = (option) => {
      const term = option.getAttribute('data-sm-term');
      if (term) {
        this.goToSearch(term); // also saves it to recent searches
        return;
      }
      if (option.matches('.predictive-search__item--term')) {
        this.goToSearch(input.value);
        return;
      }
      const link = option.matches('a[href]') ? option : option.querySelector('a[href]');
      if (link) window.location.href = link.href;
    };

    const removeRecent = (term) => {
      this.removeRecentSearch(term);
      showPanel();
      if (document.activeElement !== input) input.focus({ preventScroll: true });
    };

    input.addEventListener('focus', () => {
      if (!input.value.trim() && !isOpen()) showPanel();
    });

    // Re-open the panel when the (still focused) empty box is clicked after Escape
    input.addEventListener('click', () => {
      if (!input.value.trim() && !isOpen()) showPanel();
    });

    input.addEventListener('input', () => {
      clearTimeout(searchTimeout);
      const query = input.value.trim();

      if (!query.length) {
        showPanel();
        return;
      }

      if (query.length < 2) {
        closeResults();
        return;
      }

      searchTimeout = setTimeout(() => {
        mode = 'results';
        clearActive();
        this.performSearch(query, container, namespace, input);
      }, 250);
    });

    input.addEventListener('keydown', (e) => {
      const open = isOpen();
      const options = open ? getOptions() : [];
      if (e.key === 'ArrowDown') {
        if (!open && !input.value.trim()) {
          e.preventDefault();
          showPanel();
          return;
        }
        if (options.length) {
          e.preventDefault();
          setActive(activeIndex < 0 ? 0 : activeIndex + 1);
        }
      } else if (e.key === 'ArrowUp' && options.length) {
        e.preventDefault();
        setActive(activeIndex < 0 ? options.length - 1 : activeIndex - 1);
      } else if (e.key === 'Enter') {
        if (e.isComposing) return;
        e.preventDefault();
        if (activeIndex > -1 && options[activeIndex]) {
          activateOption(options[activeIndex]);
          return;
        }
        const query = input.value.trim();
        if (query) this.goToSearch(query);
      } else if (e.key === 'Delete' && mode === 'panel' && activeIndex > -1 && options[activeIndex] &&
                 options[activeIndex].classList.contains('sm-search-option--recent')) {
        // Delete removes the highlighted recent search
        e.preventDefault();
        const keep = activeIndex;
        removeRecent(options[activeIndex].getAttribute('data-sm-term'));
        const left = getOptions().length;
        if (left) setActive(Math.min(keep, left - 1));
      } else if (e.key === 'Escape' && open) {
        // First Escape closes the dropdown only; the next one closes the modal / drawer / panel
        e.preventDefault();
        e.stopPropagation();
        closeResults();
      }
    });

    container.addEventListener('click', (e) => {
      if (e.target.closest('[data-sm-clear-recent]')) {
        e.preventDefault();
        this.setRecentSearches([]);
        showPanel();
        input.focus({ preventScroll: true });
        return;
      }
      const remove = e.target.closest('[data-sm-remove-recent]');
      if (remove) {
        e.preventDefault();
        removeRecent(remove.getAttribute('data-sm-remove-recent'));
        return;
      }
      const option = e.target.closest('[role="option"]');
      if (option && container.contains(option)) {
        // Let Ctrl / Cmd / Shift + click open the link in a new tab natively
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        e.preventDefault();
        activateOption(option);
        return;
      }
      const link = e.target.closest('a[href]');
      if (link) window.location.href = link.href;
    });

    document.addEventListener('click', (e) => {
      // A clicked row that was just re-rendered (remove / clear) is no longer in the page
      if (!e.target.isConnected) return;
      if (!input.contains(e.target) && !container.contains(e.target)) {
        closeResults();
      }
    });
  }

  async performSearch(query, container, namespace, input) {
    namespace = namespace || 'shopdrawer';
    const requestId = (this.__searchRequestId = (this.__searchRequestId || 0) + 1);
    container.style.display = 'block';
    container.innerHTML = '<div class="search-suggestion search-loading-row">Searching…</div>';
    if (input) {
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
    }

    // "Shop parts that fit your <bike> →" row at the top of the results
    var bikeRow = this.bikeRowHtml(namespace);

    // If a vehicle is selected, tell the customer their search will be
    // filtered by it — and give them a way out. Without this, an untagged
    // universal-fit product looks like "we don't sell that".
    // Checks applied_filters (the numeric IDs MotoFit actually filters on)
    // rather than only the display labels, so the notice can't claim
    // filtering that won't really happen.
    var vehicleNotice = '';
    var v = window.motofitSelectedVehicle;
    var f = window.applied_filters;
    var canFilter = f && f.year && f.make && f.model &&
      String(f.year) !== 'null' && String(f.make) !== 'null' && String(f.model) !== 'null';
    if (canFilter && v && v.year && v.make && v.model) {
      var label = this.escapeHtml([v.year, v.make, v.model].join(' '));
      vehicleNotice =
        '<div class="search-vehicle-notice">' +
          '<span>Searching parts for your <strong>' + label + '</strong></span>' +
          '<a href="/search?q=' + encodeURIComponent(query) + '">Search all products</a>' +
        '</div>';
    }

    try {
      // Suggestions, products, collections and pages. limit_scope=each applies the
      // limit to every type (the default "all" splits it between types);
      // sections/predictive-search.liquid trims each group to its own maximum.
      const fields = 'title,body,tag,product_type,vendor,variants.title,variants.sku';
      const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
      const url = `${root}search/suggest?q=${encodeURIComponent(query)}` +
        '&resources[type]=query,product,collection,page' +
        '&resources[limit]=6' +
        '&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        `&resources[options][fields]=${fields}` +
        '&section_id=predictive-search';
      const response = await fetch(url);
      if (requestId !== this.__searchRequestId) return; // a newer keystroke superseded this request
      if (!response.ok) throw new Error(response.status);

      const text = await response.text();
      if (requestId !== this.__searchRequestId) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');
      let html = section ? section.innerHTML : '';

      // Namespace ids/aria refs so the same section markup can safely
      // live in the desktop bar, mobile modal and mobile drawer at once.
      html = html.replace(/(id|aria-labelledby|aria-owns|aria-controls)="(predictive-search[\w-]*)"/g, (m, attr, id) => `${attr}="${namespace}-${id}"`);

      if (!html) {
        container.innerHTML = bikeRow + vehicleNotice + `
          <div class="search-empty-state">
            <p class="search-empty-state__title">No matches for “${this.escapeHtml(query)}”</p>
            <p class="search-empty-state__hint">Try the item name, brand, or part number — or check the spelling.</p>
            <a class="search-empty-state__link" href="/search?q=${encodeURIComponent(query)}">Search the full catalog for “${this.escapeHtml(query)}” →</a>
          </div>`;
        return;
      }

      container.innerHTML = bikeRow + vehicleNotice + html;

      // The search box's own container is the listbox; the injected wrapper is only layout.
      const inner = container.querySelector('[id$="predictive-search-results"]');
      if (inner) {
        inner.setAttribute('role', 'presentation');
        inner.removeAttribute('aria-label');
      }
    } catch (error) {
      if (requestId !== this.__searchRequestId) return;
      console.error('Search error:', error);
      container.innerHTML = bikeRow + '<div class="search-suggestion">Search is temporarily unavailable. Press Enter to try the full search page.</div>';
    }
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  setupCartUpdates() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      this.updateCartCount();
    });

    // Taste 16: product-form.js, cart.js, quick-add-bulk etc. announce cart
    // changes through pubsub.js instead of the old 'cart:updated' DOM event.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.updateCartCount());
    }

    // Initial cart count update
    this.updateCartCount();
  }

  updateCartCount() {
    const cartIcon = document.getElementById('cart-icon');
    if (cartIcon) {
      /* CHANGED: excluded product id now comes from section settings
         instead of being hardcoded in JS */
      const EXCLUDED_PRODUCT_ID = Number(window.smSurchargeId) || 8156021653712;
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartCount = cartIcon.querySelector('.cart-count');
          let itemCount = 0;

          cart.items.forEach(item => {
            if (item.product_id !== EXCLUDED_PRODUCT_ID) {
              itemCount += item.quantity;
            }
          });

          if (itemCount > 0) {
            if (!cartCount) {
              const countElement = document.createElement('span');
              countElement.className = 'cart-count';
              cartIcon.appendChild(countElement);
            }
            cartIcon.querySelector('.cart-count').textContent = itemCount;
          } else if (cartCount) {
            cartCount.remove();
          }
        })
        .catch(error => console.error('Cart update error:', error));
    }
  }

  /* Header that comes back on scroll up: it sticks to the top (CSS: html.sm-hdr-reveal), slides away while
     the shopper scrolls down and slides back as soon as they scroll up, so search, menu and cart are one
     swipe away on long pages. --sm-header-h is the height it covers right now (0 while hidden): the filter
     sidebar / bar, blog sidebar and product photo column use it to sit just below it. */
  setupRevealHeader() {
    const section = document.querySelector('.section-header.shopify-section-group-header-group');
    if (!section) return;
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('sm-hdr-reveal');

    // Where the header sits in the page (below the announcement bar), measured while not stuck
    const naturalTop = () => {
      const prev = section.previousElementSibling;
      return prev ? prev.offsetTop + prev.offsetHeight : 0;
    };
    let top = naturalTop();
    let lastY = window.scrollY;
    let hidden = false;
    let ticking = false;

    const apply = () => {
      const y = window.scrollY;
      const h = section.offsetHeight;
      const stuck = y > top;
      root.classList.toggle('sm-hdr-stuck', stuck);
      root.classList.toggle('sm-hdr-hidden', hidden);
      root.style.setProperty('--sm-header-h', (stuck && !hidden ? h : 0) + 'px');
    };

    const update = () => {
      ticking = false;
      const y = window.scrollY;
      // Leave it alone while a drawer or menu holds the page
      if (body.classList.contains('drawer-open') || body.classList.contains('mega-menu-open') ||
          section.querySelector('.nav-item:hover > .mega-menu, .nav-item.sm-kb-open')) {
        lastY = y;
        return;
      }
      const h = section.offsetHeight;
      if (y <= top + h + 40) hidden = false;
      else if (y > lastY + 6) hidden = true;
      else if (y < lastY - 6) hidden = false;
      lastY = y;
      apply();
    };

    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', () => { top = naturalTop(); apply(); }, { passive: true });
    // Keyboard users tabbing into the header always get it back
    section.addEventListener('focusin', () => { if (hidden) { hidden = false; apply(); } });
    apply();
  }

  setupStickyHeader() {
    const headerSection = document.getElementById('shopify-section-header')
      || document.querySelector('[id^="shopify-section-"][id*="__header"]');
    if (!headerSection) return;

    const root = document.documentElement;
    const body = document.body;
    const tertiaryBar = document.querySelector('.header-tertiary');
    let isSticky = false;
    let lastScrollY = window.scrollY;
    let tertiaryHidden = false;

    const setHeaderHeight = () => {
      const height = headerSection.offsetHeight;
      root.style.setProperty('--modern-header-height', `${height}px`);
      return height;
    };

    const enableSticky = () => {
      if (isSticky) return;
      isSticky = true;
      setHeaderHeight();
      headerSection.classList.add('is-sticky');
      // Only reserve space (body padding) when the header really left the page flow;
      // otherwise the padding pushes the page down 125px on scroll (layout shift / CLS).
      const pos = getComputedStyle(headerSection).position;
      if (pos === 'fixed' || pos === 'absolute') body.classList.add('header-sticky-active');
    };

    const disableSticky = () => {
      if (!isSticky) return;
      isSticky = false;
      headerSection.classList.remove('is-sticky');
      body.classList.remove('header-sticky-active');
      root.style.setProperty('--modern-header-height', '0px');
    };

    const hideTertiary = () => {
      if (!tertiaryBar || tertiaryHidden) return;
      tertiaryHidden = true;
      tertiaryBar.classList.add('is-hidden');
    };

    const showTertiary = () => {
      if (!tertiaryBar || !tertiaryHidden) return;
      tertiaryHidden = false;
      tertiaryBar.classList.remove('is-hidden');
    };

    const handleScroll = () => {
      const currentY = window.scrollY;

      if (currentY > 0) {
        enableSticky();
      } else {
        disableSticky();
      }

      if (tertiaryBar) {
        const scrollingDown = currentY > lastScrollY;
        const scrollingUp = currentY < lastScrollY;

        if (scrollingDown && currentY > 120) {
          hideTertiary();
        } else if (scrollingUp) {
          showTertiary();
        }

        if (currentY <= 40) {
          showTertiary();
        }
      }

      lastScrollY = currentY;
    };

    setHeaderHeight();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', () => {
      if (isSticky) {
        setHeaderHeight();
      }
    });
    handleScroll();
  }

  setupYmmMegaMenu() {
    const ymmTrigger = document.querySelector('.ymm-mega-menu-trigger');
    const ymmMegaMenu = document.querySelector('.ymm-mega-menu');
    const headerNavigation = document.querySelector('.header-navigation');
    const body = document.body;
    let scrollPosition = 0;

    const preventScroll = () => {
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      body.classList.add('mega-menu-open');
      body.style.top = `-${scrollPosition}px`;
    };

    const allowScroll = () => {
      body.classList.remove('mega-menu-open');
      body.style.top = '';
      window.scrollTo(0, scrollPosition);
    };

    if (ymmTrigger && ymmMegaMenu) {
      const navItem = ymmTrigger.closest('.nav-item');

      // Calculate and set top position dynamically
      const updateMegaMenuPosition = () => {
        if (window.innerWidth > 768 && headerNavigation) {
          const headerRect = headerNavigation.getBoundingClientRect();
          const megaMenuTop = headerRect.bottom;
          ymmMegaMenu.style.top = megaMenuTop + 'px';
        }
      };

      // Update position on load and scroll
      updateMegaMenuPosition();

      let scrollTicking = false;
      const handleScroll = () => {
        if (!scrollTicking) {
          window.requestAnimationFrame(() => {
            updateMegaMenuPosition();
            scrollTicking = false;
          });
          scrollTicking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', updateMegaMenuPosition);

      // Keep position fresh around hover (desktop)
      if (window.innerWidth > 768 && navItem) {
        const handleMouseEnter = () => {
          updateMegaMenuPosition();
        };

        const handleMouseLeave = () => {
          updateMegaMenuPosition();
        };

        navItem.addEventListener('mouseenter', handleMouseEnter);
        navItem.addEventListener('mouseleave', handleMouseLeave);
        ymmMegaMenu.addEventListener('mouseenter', handleMouseEnter);
        ymmMegaMenu.addEventListener('mouseleave', handleMouseLeave);
      }

      // Mobile: toggle on click
      if (window.innerWidth <= 768) {
        ymmTrigger.addEventListener('click', (e) => {
          e.preventDefault();
          const isVisible = ymmMegaMenu.style.display === 'block' ||
                           ymmMegaMenu.classList.contains('active');
          if (isVisible) {
            ymmMegaMenu.style.display = 'none';
            ymmMegaMenu.classList.remove('active');
            allowScroll();
          } else {
            ymmMegaMenu.style.display = 'block';
            ymmMegaMenu.classList.add('active');
            preventScroll();
          }
        });

        // Close mega menu when clicking outside
        document.addEventListener('click', (e) => {
          if (!ymmMegaMenu.contains(e.target) && !ymmTrigger.contains(e.target)) {
            ymmMegaMenu.style.display = 'none';
            ymmMegaMenu.classList.remove('active');
            allowScroll();
          }
        });
      }
    }
  }

  setupCategoryMegaMenu() {
    const megaMenuTriggers = document.querySelectorAll('.mega-menu-trigger');
    const headerNavigation = document.querySelector('.header-navigation');
    const body = document.body;
    let scrollPosition = 0;

    const preventScroll = () => {
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      body.classList.add('mega-menu-open');
      body.style.top = `-${scrollPosition}px`;
    };

    const allowScroll = () => {
      body.classList.remove('mega-menu-open');
      body.style.top = '';
      window.scrollTo(0, scrollPosition);
    };

    megaMenuTriggers.forEach((megaMenuTrigger) => {
      const menuId = megaMenuTrigger.dataset.megaMenuId;
      const navItem = megaMenuTrigger.closest('.nav-item');
      const megaMenu = navItem ? navItem.querySelector(`.mega-menu[data-mega-menu-id="${menuId}"]`) : null;

      if (!megaMenu) return;

      // Calculate and set top position dynamically for desktop
      const updateMegaMenuPosition = () => {
        if (window.innerWidth > 768 && headerNavigation) {
          const headerRect = headerNavigation.getBoundingClientRect();
          const megaMenuTop = headerRect.bottom;
          megaMenu.style.top = megaMenuTop + 'px';
        }
      };

      // Update position on load and scroll
      updateMegaMenuPosition();

      let scrollTicking = false;
      const handleScroll = () => {
        if (!scrollTicking) {
          window.requestAnimationFrame(() => {
            updateMegaMenuPosition();
            scrollTicking = false;
          });
          scrollTicking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', updateMegaMenuPosition);

      // Keep position fresh around hover (desktop)
      if (window.innerWidth > 768) {
        const handleMouseEnter = () => {
          updateMegaMenuPosition();
        };

        const handleMouseLeave = () => {
          updateMegaMenuPosition();
        };

        navItem.addEventListener('mouseenter', handleMouseEnter);
        navItem.addEventListener('mouseleave', handleMouseLeave);
        megaMenu.addEventListener('mouseenter', handleMouseEnter);
        megaMenu.addEventListener('mouseleave', handleMouseLeave);
      }

      /* CHANGED: carousel now supports ANY number of slides, not just 2.
         Slide count is read from the DOM instead of being hardcoded. */
      const slides = megaMenu.querySelectorAll('.mega-slide');
      if (slides.length > 1) {
        let currentSlide = 1;
        const totalSlides = slides.length;

        const prevBtn = megaMenu.querySelector(`[data-mega-nav="prev"][data-mega-menu-id="${menuId}"]`);
        const nextBtn = megaMenu.querySelector(`[data-mega-nav="next"][data-mega-menu-id="${menuId}"]`);
        const indicators = megaMenu.querySelectorAll('.mega-indicator');

        const updateCarousel = () => {
          // Update slides within this mega menu only
          slides.forEach(slide => {
            slide.classList.remove('mega-slide-active');
          });
          const activeSlide = megaMenu.querySelector(`.mega-slide[data-slide="${currentSlide}"]`);
          if (activeSlide) {
            activeSlide.classList.add('mega-slide-active');
          }

          // Update indicators within this mega menu
          indicators.forEach(indicator => {
            indicator.classList.remove('active');
          });
          const activeIndicator = megaMenu.querySelector(`.mega-indicator[data-slide="${currentSlide}"]`);
          if (activeIndicator) {
            activeIndicator.classList.add('active');
          }
          indicators.forEach(indicator => {
            indicator.setAttribute('aria-current', indicator === activeIndicator ? 'true' : 'false');
          });

          // Update navigation buttons
          if (prevBtn) {
            prevBtn.disabled = currentSlide === 1;
          }
          if (nextBtn) {
            nextBtn.disabled = currentSlide === totalSlides;
          }
        };

        // Navigation button event listeners
        if (prevBtn) {
          prevBtn.addEventListener('click', () => {
            if (currentSlide > 1) {
              currentSlide--;
              updateCarousel();
            }
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', () => {
            if (currentSlide < totalSlides) {
              currentSlide++;
              updateCarousel();
            }
          });
        }

        // Indicator event listeners
        indicators.forEach(indicator => {
          indicator.addEventListener('click', () => {
            currentSlide = parseInt(indicator.dataset.slide);
            updateCarousel();
          });
        });

        // Initialize carousel
        updateCarousel();
      }

    });
  }

  setupBrandMegaMenu() {
    const brandTrigger = document.querySelector('.brand-mega-menu-trigger');
    const brandMegaMenu = document.querySelector('.brand-mega-menu');
    const headerNavigation = document.querySelector('.header-navigation');
    const body = document.body;
    let scrollPosition = 0;

    const preventScroll = () => {
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      body.classList.add('mega-menu-open');
      body.style.top = `-${scrollPosition}px`;
    };

    const allowScroll = () => {
      body.classList.remove('mega-menu-open');
      body.style.top = '';
      window.scrollTo(0, scrollPosition);
    };

    if (brandTrigger && brandMegaMenu) {
      const navItem = brandTrigger.closest('.nav-item');

      // Calculate and set top position dynamically
      const updateBrandMegaMenuPosition = () => {
        if (window.innerWidth > 768 && headerNavigation) {
          const headerRect = headerNavigation.getBoundingClientRect();
          const megaMenuTop = headerRect.bottom;
          brandMegaMenu.style.top = megaMenuTop + 'px';
        }
      };

      // Update position on load and scroll
      updateBrandMegaMenuPosition();

      let scrollTicking = false;
      const handleScroll = () => {
        if (!scrollTicking) {
          window.requestAnimationFrame(() => {
            updateBrandMegaMenuPosition();
            scrollTicking = false;
          });
          scrollTicking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', updateBrandMegaMenuPosition);

      // Keep position fresh around hover (desktop)
      if (window.innerWidth > 768 && navItem) {
        const handleMouseEnter = () => {
          updateBrandMegaMenuPosition();
        };

        const handleMouseLeave = () => {
          updateBrandMegaMenuPosition();
        };

        navItem.addEventListener('mouseenter', handleMouseEnter);
        navItem.addEventListener('mouseleave', handleMouseLeave);
        brandMegaMenu.addEventListener('mouseenter', handleMouseEnter);
        brandMegaMenu.addEventListener('mouseleave', handleMouseLeave);
      }

      // Mobile: toggle on click
      if (window.innerWidth <= 768) {
        brandTrigger.addEventListener('click', (e) => {
          e.preventDefault();
          const isVisible = brandMegaMenu.style.display === 'block' ||
                           brandMegaMenu.classList.contains('active');
          if (isVisible) {
            brandMegaMenu.style.display = 'none';
            brandMegaMenu.classList.remove('active');
            allowScroll();
          } else {
            brandMegaMenu.style.display = 'block';
            brandMegaMenu.classList.add('active');
            preventScroll();
          }
        });

        // Close mega menu when clicking outside
        document.addEventListener('click', (e) => {
          if (!brandMegaMenu.contains(e.target) && !brandTrigger.contains(e.target)) {
            brandMegaMenu.style.display = 'none';
            brandMegaMenu.classList.remove('active');
            allowScroll();
          }
        });
      }
    }
  }

  setupBottomNav() {
    // Only setup on mobile
    if (window.innerWidth > 768) return;

    // Move mobile bottom nav to body level to ensure proper positioning
    const mobileBottomNav = document.getElementById('mobile-bottom-nav');
    if (mobileBottomNav && mobileBottomNav.parentElement !== document.body) {
      document.body.appendChild(mobileBottomNav);
    }

    const bottomNavItems = document.querySelectorAll('.mobile-bottom-nav-item');
    const body = document.body;
    let scrollPosition = 0;

    const preventScroll = () => {
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      body.classList.add('mega-menu-open');
      body.style.top = `-${scrollPosition}px`;
    };

    const allowScroll = () => {
      body.classList.remove('mega-menu-open');
      body.style.top = '';
      window.scrollTo(0, scrollPosition);
    };

    bottomNavItems.forEach((item) => {
      const triggerType = item.dataset.triggerType;
      const menuId = item.dataset.megaMenuId;

      item.addEventListener('click', (e) => {
        e.preventDefault();

        // Remove active class from all items
        bottomNavItems.forEach(navItem => navItem.classList.remove('active'));
        // Add active class to clicked item
        item.classList.add('active');

        if (triggerType === 'shop') {
          // Trigger Shop drawer
          this.toggleShopDrawer();
        } else if (triggerType === 'vehicle') {
          // Trigger Vehicle drawer
          this.toggleVehicleDrawer();
        } else if (triggerType === 'brand-drawer') {
          // Trigger Brand drawer
          this.toggleBrandDrawer();
        } else if (triggerType === 'ymm') {
          // Close shop drawer if open
          const shopDrawer = document.getElementById('shop-drawer');
          if (shopDrawer && shopDrawer.classList.contains('active')) {
            this.toggleShopDrawer();
          }

          // Close vehicle drawer if open
          const vehicleDrawer = document.getElementById('vehicle-drawer');
          if (vehicleDrawer && vehicleDrawer.classList.contains('active')) {
            this.toggleVehicleDrawer();
          }

          // Close brand drawer if open
          const brandDrawer = document.getElementById('brand-drawer');
          if (brandDrawer && brandDrawer.classList.contains('active')) {
            this.toggleBrandDrawer();
          }

          // Trigger YMM mega menu
          const ymmTrigger = document.querySelector('.ymm-mega-menu-trigger');
          const ymmMegaMenu = document.querySelector('.ymm-mega-menu');

          if (ymmTrigger && ymmMegaMenu) {
            const isVisible = ymmMegaMenu.style.display === 'block' ||
                             ymmMegaMenu.classList.contains('active');
            if (isVisible) {
              ymmMegaMenu.style.display = 'none';
              ymmMegaMenu.classList.remove('active');
              allowScroll();
              item.classList.remove('active');
            } else {
              ymmMegaMenu.style.display = 'block';
              ymmMegaMenu.classList.add('active');
              preventScroll();
            }
          }
        } else if (triggerType === 'brand') {
          // Close shop drawer if open
          const shopDrawer = document.getElementById('shop-drawer');
          if (shopDrawer && shopDrawer.classList.contains('active')) {
            this.toggleShopDrawer();
          }

          // Close vehicle drawer if open
          const vehicleDrawer = document.getElementById('vehicle-drawer');
          if (vehicleDrawer && vehicleDrawer.classList.contains('active')) {
            this.toggleVehicleDrawer();
          }

          // Close brand drawer if open
          const brandDrawer = document.getElementById('brand-drawer');
          if (brandDrawer && brandDrawer.classList.contains('active')) {
            this.toggleBrandDrawer();
          }

          // Trigger Brand mega menu
          const brandTrigger = document.querySelector('.brand-mega-menu-trigger');
          const brandMegaMenu = document.querySelector('.brand-mega-menu');

          if (brandTrigger && brandMegaMenu) {
            const isVisible = brandMegaMenu.style.display === 'block' ||
                             brandMegaMenu.classList.contains('active');
            if (isVisible) {
              brandMegaMenu.style.display = 'none';
              brandMegaMenu.classList.remove('active');
              allowScroll();
              item.classList.remove('active');
            } else {
              brandMegaMenu.style.display = 'block';
              brandMegaMenu.classList.add('active');
              preventScroll();
            }
          }
        } else if (triggerType === 'category' && menuId) {
          // Close shop drawer if open
          const shopDrawer = document.getElementById('shop-drawer');
          if (shopDrawer && shopDrawer.classList.contains('active')) {
            this.toggleShopDrawer();
          }

          // Close vehicle drawer if open
          const vehicleDrawer = document.getElementById('vehicle-drawer');
          if (vehicleDrawer && vehicleDrawer.classList.contains('active')) {
            this.toggleVehicleDrawer();
          }

          // Close brand drawer if open
          const brandDrawer = document.getElementById('brand-drawer');
          if (brandDrawer && brandDrawer.classList.contains('active')) {
            this.toggleBrandDrawer();
          }

          // Trigger Category mega menu
          const megaMenu = document.querySelector(`.mega-menu[data-mega-menu-id="${menuId}"]`);

          if (megaMenu) {
            const isVisible = megaMenu.style.display === 'block' ||
                             megaMenu.classList.contains('active');
            if (isVisible) {
              megaMenu.style.display = 'none';
              megaMenu.classList.remove('active');
              allowScroll();
              item.classList.remove('active');
            } else {
              megaMenu.style.display = 'block';
              megaMenu.classList.add('active');
              preventScroll();
            }
          }
        } else {
          // Close shop drawer if open for other items (Account, Services)
          const shopDrawer = document.getElementById('shop-drawer');
          if (shopDrawer && shopDrawer.classList.contains('active')) {
            this.toggleShopDrawer();
          }

          // Close vehicle drawer if open
          const vehicleDrawer = document.getElementById('vehicle-drawer');
          if (vehicleDrawer && vehicleDrawer.classList.contains('active')) {
            this.toggleVehicleDrawer();
          }

          // Close brand drawer if open
          const brandDrawer = document.getElementById('brand-drawer');
          if (brandDrawer && brandDrawer.classList.contains('active')) {
            this.toggleBrandDrawer();
          }
        }
      });
    });

    // Close mega menus when clicking outside
    document.addEventListener('click', (e) => {
      const bottomNav = document.getElementById('mobile-bottom-nav');
      if (!bottomNav || !bottomNav.contains(e.target)) {
        // Check if click is outside any mega menu
        const ymmMenu = document.querySelector('.ymm-mega-menu');
        const brandMenu = document.querySelector('.brand-mega-menu');
        const categoryMenus = document.querySelectorAll('.mega-menu');

        let clickedInsideMenu = false;

        if (ymmMenu && ymmMenu.contains(e.target)) clickedInsideMenu = true;
        if (brandMenu && brandMenu.contains(e.target)) clickedInsideMenu = true;
        categoryMenus.forEach(menu => {
          if (menu.contains(e.target)) clickedInsideMenu = true;
        });

        if (!clickedInsideMenu) {
          // Close all mega menus
          if (ymmMenu) {
            ymmMenu.style.display = 'none';
            ymmMenu.classList.remove('active');
          }
          if (brandMenu) {
            brandMenu.style.display = 'none';
            brandMenu.classList.remove('active');
          }
          categoryMenus.forEach(menu => {
            menu.style.display = 'none';
            menu.classList.remove('active');
          });

          // Remove active class from bottom nav items
          bottomNavItems.forEach(navItem => navItem.classList.remove('active'));
          allowScroll();
        }
      }
    });

    // Set active state based on current page
    bottomNavItems.forEach((item) => {
      if (item.href && item.href !== 'javascript:void(0)') {
        const currentUrl = window.location.pathname;
        const itemUrl = new URL(item.href, window.location.origin).pathname;
        if (currentUrl === itemUrl || currentUrl.startsWith(itemUrl + '/')) {
          item.classList.add('active');
        }
      }
    });
  }

  setupMobileInteractions() {
    const closeMobileMenu = (menu) => {
      if (!menu) return;
      menu.classList.remove('active');
      document.body.style.overflow = '';
      document.body.classList.remove('mega-menu-open');
    };

    const openMobileMenu = (menu) => {
      if (!menu) return;
      // Close any other open menus first
      document.querySelectorAll('.mega-menu.active, .brand-mega-menu.active, .ymm-mega-menu.active').forEach(m => {
        if (m !== menu) closeMobileMenu(m);
      });
      menu.classList.add('active');
      // Prevent body scroll when mega menu is open
      document.body.style.overflow = 'hidden';
      document.body.classList.add('mega-menu-open');
      // a11y: move focus into the drill-down panel (its Back button)
      const back = menu.querySelector('.mobile-menu-back');
      if (back) window.requestAnimationFrame(() => back.focus({ preventScroll: true }));
    };

    const triggerFor = (menu) => {
      const navItem = menu && menu.closest('.nav-item');
      return navItem ? navItem.querySelector('.mega-menu-trigger, .brand-mega-menu-trigger, .ymm-mega-menu-trigger') : null;
    };

    // a11y: Escape closes an open drill-down panel and returns focus to its trigger
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || window.innerWidth > 768) return;
      const open = document.querySelector('.nav-menu .mega-menu.active, .nav-menu .brand-mega-menu.active, .nav-menu .ymm-mega-menu.active');
      if (!open) return;
      closeMobileMenu(open);
      const trigger = triggerFor(open);
      if (trigger) {
        trigger.classList.remove('active');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus({ preventScroll: true });
      }
    });

    // Global event delegation for mobile menu interactions
    document.addEventListener('click', (e) => {
      // Check if mobile
      const isMobile = window.innerWidth <= 768;

      // 1. Back Button Handler
      const backBtn = e.target.closest('.mobile-menu-back');
      if (backBtn) {
        e.preventDefault();
        e.stopPropagation();

        const parentMenu = backBtn.closest('.mega-menu, .brand-mega-menu, .ymm-mega-menu');
        if (parentMenu) {
          closeMobileMenu(parentMenu);

          const navItem = parentMenu.closest('.nav-item');
          if (navItem) {
            const trigger = navItem.querySelector('.mega-menu-trigger, .brand-mega-menu-trigger, .ymm-mega-menu-trigger');
            if (trigger) {
              trigger.classList.remove('active');
              trigger.setAttribute('aria-expanded', 'false');
              trigger.focus({ preventScroll: true });
            }
          }
        }
        return;
      }

      // Only process menu triggers on mobile
      if (!isMobile) return;

      // 2. Category Mega Menu Trigger (Parts, Gear, Maintenance)
      const catTrigger = e.target.closest('.mega-menu-trigger');
      if (catTrigger) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const navItem = catTrigger.closest('.nav-item');
        if (!navItem) return;

        const menuId = catTrigger.dataset.megaMenuId;
        const megaMenu = navItem.querySelector(`.mega-menu[data-mega-menu-id="${menuId}"]`);

        if (megaMenu) {
          catTrigger.classList.add('active');
          catTrigger.setAttribute('aria-expanded', 'true');
          openMobileMenu(megaMenu);
        }
        return false;
      }

      // 3. Brand Mega Menu Trigger
      const brandTrigger = e.target.closest('.brand-mega-menu-trigger');
      if (brandTrigger) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const navItem = brandTrigger.closest('.nav-item');
        if (!navItem) return;

        const brandMenu = navItem.querySelector('.brand-mega-menu');

        if (brandMenu) {
          brandTrigger.classList.add('active');
          brandTrigger.setAttribute('aria-expanded', 'true');
          openMobileMenu(brandMenu);
        }
        return false;
      }

      // 4. YMM Mega Menu Trigger
      const ymmTrigger = e.target.closest('.ymm-mega-menu-trigger');
      if (ymmTrigger) {
         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation();

         const navItem = ymmTrigger.closest('.nav-item');
         if (!navItem) return;

         const ymmMenu = navItem.querySelector('.ymm-mega-menu');

         if (ymmMenu) {
           ymmTrigger.classList.add('active');
           ymmTrigger.setAttribute('aria-expanded', 'true');
           openMobileMenu(ymmMenu);
         }
         return false;
      }
    }, true); // Use capture phase to ensure we get the event first
  }

  setupShopDrawer() {
    const shopDrawer = document.getElementById('shop-drawer');
    const shopDrawerTriggers = document.querySelectorAll('.shop-drawer-trigger');
    const shopDrawerClose = document.getElementById('shop-drawer-close');
    const shopDrawerOverlay = shopDrawer?.querySelector('.shop-drawer-overlay');
    const shopDrawerSearchInput = document.getElementById('shop-drawer-search-input');
    const shopDrawerSearchSuggestions = document.getElementById('shop-drawer-search-suggestions');
    const body = document.body;
    let scrollPosition = 0;

    if (!shopDrawer) return;

    const openDrawer = () => {
      // Close any open mega menus
      const ymmMenu = document.querySelector('.ymm-mega-menu');
      const brandMenu = document.querySelector('.brand-mega-menu');
      const categoryMenus = document.querySelectorAll('.mega-menu');

      if (ymmMenu) {
        ymmMenu.style.display = 'none';
        ymmMenu.classList.remove('active');
      }
      if (brandMenu) {
        brandMenu.style.display = 'none';
        brandMenu.classList.remove('active');
      }
      categoryMenus.forEach(menu => {
        menu.style.display = 'none';
        menu.classList.remove('active');
      });

      // Close vehicle drawer if open
      const vehicleDrawer = document.getElementById('vehicle-drawer');
      if (vehicleDrawer && vehicleDrawer.classList.contains('active')) {
        this.toggleVehicleDrawer();
      }

      // Close brand drawer if open
      const brandDrawer = document.getElementById('brand-drawer');
      if (brandDrawer && brandDrawer.classList.contains('active')) {
        this.toggleBrandDrawer();
      }

      // Remove active from bottom nav items
      const bottomNavItems = document.querySelectorAll('.mobile-bottom-nav-item');
      bottomNavItems.forEach(item => item.classList.remove('active'));

      // Add active to shop triggers
      shopDrawerTriggers.forEach(trigger => trigger.classList.add('active'));

      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      shopDrawer.classList.add('active');
      body.classList.add('shop-drawer-open');

      // Remove focus to avoid mobile keyboard/scroll jumps
      if (shopDrawerSearchInput) {
        shopDrawerSearchInput.blur();
      }
    };

    const closeDrawer = () => {
      shopDrawer.classList.remove('active');
      body.classList.remove('shop-drawer-open');

      // Remove active from shop triggers
      shopDrawerTriggers.forEach(trigger => trigger.classList.remove('active'));

      // Clear search
      if (shopDrawerSearchInput) {
        shopDrawerSearchInput.value = '';
      }
      if (shopDrawerSearchSuggestions) {
        shopDrawerSearchSuggestions.style.display = 'none';
      }
    };

    // Open drawer
    shopDrawerTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (shopDrawer.classList.contains('active')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    });

    // Close drawer
    if (shopDrawerClose) {
      shopDrawerClose.addEventListener('click', closeDrawer);
    }

    // Close on overlay click
    if (shopDrawerOverlay) {
      shopDrawerOverlay.addEventListener('click', closeDrawer);
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && shopDrawer.classList.contains('active')) {
        closeDrawer();
      }
    });

    // Search functionality
    if (shopDrawerSearchInput && shopDrawerSearchSuggestions) {
      let searchTimeout;

      shopDrawerSearchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        const query = e.target.value.trim();

        if (query.length < 2) {
          shopDrawerSearchSuggestions.style.display = 'none';
          return;
        }

        searchTimeout = setTimeout(() => {
          this.performSearch(query, shopDrawerSearchSuggestions);
        }, 300);
      });

      // Hide suggestions when clicking outside
      document.addEventListener('click', (e) => {
        if (!shopDrawerSearchInput.contains(e.target) && !shopDrawerSearchSuggestions.contains(e.target)) {
          shopDrawerSearchSuggestions.style.display = 'none';
        }
      });
    }

    // Store methods for external access
    this.toggleShopDrawer = () => {
      if (shopDrawer.classList.contains('active')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    };
  }

  setupVehicleDrawer() {
    const vehicleDrawer = document.getElementById('vehicle-drawer');
    const vehicleDrawerTriggers = document.querySelectorAll('.vehicle-drawer-trigger');
    const vehicleDrawerClose = document.getElementById('vehicle-drawer-close');
    const vehicleDrawerOverlay = vehicleDrawer?.querySelector('.vehicle-drawer-overlay');
    const body = document.body;
    let scrollPosition = 0;

    if (!vehicleDrawer) return;

    const openDrawer = () => {
      // Close any open mega menus
      const ymmMenu = document.querySelector('.ymm-mega-menu');
      const brandMenu = document.querySelector('.brand-mega-menu');
      const categoryMenus = document.querySelectorAll('.mega-menu');

      if (ymmMenu) {
        ymmMenu.style.display = 'none';
        ymmMenu.classList.remove('active');
      }
      if (brandMenu) {
        brandMenu.style.display = 'none';
        brandMenu.classList.remove('active');
      }
      categoryMenus.forEach(menu => {
        menu.style.display = 'none';
        menu.classList.remove('active');
      });

      // Close shop drawer if open
      const shopDrawer = document.getElementById('shop-drawer');
      if (shopDrawer && shopDrawer.classList.contains('active')) {
        this.toggleShopDrawer();
      }

      // Close brand drawer if open
      const brandDrawer = document.getElementById('brand-drawer');
      if (brandDrawer && brandDrawer.classList.contains('active')) {
        this.toggleBrandDrawer();
      }

      // Remove active from bottom nav items
      const bottomNavItems = document.querySelectorAll('.mobile-bottom-nav-item');
      bottomNavItems.forEach(item => item.classList.remove('active'));

      // Add active to vehicle triggers
      vehicleDrawerTriggers.forEach(trigger => trigger.classList.add('active'));

      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      vehicleDrawer.classList.add('active');
      body.classList.add('vehicle-drawer-open');
    };

    const closeDrawer = () => {
      vehicleDrawer.classList.remove('active');
      body.classList.remove('vehicle-drawer-open');

      // Remove active from vehicle triggers
      vehicleDrawerTriggers.forEach(trigger => trigger.classList.remove('active'));
    };

    // Open drawer
    vehicleDrawerTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (vehicleDrawer.classList.contains('active')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    });

    // Close drawer
    if (vehicleDrawerClose) {
      vehicleDrawerClose.addEventListener('click', closeDrawer);
    }

    // Close on overlay click
    if (vehicleDrawerOverlay) {
      vehicleDrawerOverlay.addEventListener('click', closeDrawer);
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && vehicleDrawer.classList.contains('active')) {
        closeDrawer();
      }
    });

    // Store methods for external access
    this.toggleVehicleDrawer = () => {
      if (vehicleDrawer.classList.contains('active')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    };
  }

  setupBrandDrawer() {
    const brandDrawer = document.getElementById('brand-drawer');
    const brandDrawerTriggers = document.querySelectorAll('.brand-drawer-trigger');
    const brandDrawerClose = document.getElementById('brand-drawer-close');
    const brandDrawerOverlay = brandDrawer?.querySelector('.brand-drawer-overlay');
    const brandSearchInput = document.getElementById('brand-drawer-search-input');
    const brandListItems = document.querySelectorAll('.brand-list-item');
    const brandPopularLinks = document.querySelectorAll('.brand-popular-link');
    const body = document.body;
    let scrollPosition = 0;

    if (!brandDrawer) return;

    const openDrawer = () => {
      // Close any open mega menus
      const ymmMenu = document.querySelector('.ymm-mega-menu');
      const brandMenu = document.querySelector('.brand-mega-menu');
      const categoryMenus = document.querySelectorAll('.mega-menu');

      if (ymmMenu) {
        ymmMenu.style.display = 'none';
        ymmMenu.classList.remove('active');
      }
      if (brandMenu) {
        brandMenu.style.display = 'none';
        brandMenu.classList.remove('active');
      }
      categoryMenus.forEach(menu => {
        menu.style.display = 'none';
        menu.classList.remove('active');
      });

      // Close shop drawer if open
      const shopDrawer = document.getElementById('shop-drawer');
      if (shopDrawer && shopDrawer.classList.contains('active')) {
        this.toggleShopDrawer();
      }

      // Close vehicle drawer if open
      const vehicleDrawer = document.getElementById('vehicle-drawer');
      if (vehicleDrawer && vehicleDrawer.classList.contains('active')) {
        this.toggleVehicleDrawer();
      }

      // Remove active from bottom nav items
      const bottomNavItems = document.querySelectorAll('.mobile-bottom-nav-item');
      bottomNavItems.forEach(item => item.classList.remove('active'));

      // Add active to brand triggers
      brandDrawerTriggers.forEach(trigger => trigger.classList.add('active'));

      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      brandDrawer.classList.add('active');
      body.classList.add('brand-drawer-open');

      // Remove focus to avoid mobile keyboard/scroll jumps
      if (brandSearchInput) {
        brandSearchInput.blur();
      }
    };

    const closeDrawer = () => {
      brandDrawer.classList.remove('active');
      body.classList.remove('brand-drawer-open');

      // Remove active from brand triggers
      brandDrawerTriggers.forEach(trigger => trigger.classList.remove('active'));

      // Clear search
      if (brandSearchInput) {
        brandSearchInput.value = '';
        filterBrands();
      }
    };

    // Filter brands function
    const filterBrands = () => {
      if (!brandSearchInput) return;

      const filter = brandSearchInput.value.toUpperCase();

      // Filter brand list items
      if (window.smFillBrandList) window.smFillBrandList();
      document.querySelectorAll('.brand-list-item').forEach(item => {
        const brandName = item.dataset.brandName || '';
        const brandLink = item.querySelector('.brand-link');
        const text = brandLink ? (brandLink.textContent || brandLink.innerText) : '';

        if (text.toUpperCase().indexOf(filter) > -1 || brandName.toUpperCase().indexOf(filter) > -1) {
          item.classList.remove('hidden');
        } else {
          item.classList.add('hidden');
        }
      });

      // Filter popular brands
      brandPopularLinks.forEach(link => {
        const brandName = link.dataset.brandName || '';
        const text = link.textContent || link.innerText;

        if (text.toUpperCase().indexOf(filter) > -1 || brandName.toUpperCase().indexOf(filter) > -1) {
          link.style.display = '';
        } else {
          link.style.display = 'none';
        }
      });
    };

    // Open drawer
    brandDrawerTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (brandDrawer.classList.contains('active')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    });

    // Close drawer
    if (brandDrawerClose) {
      brandDrawerClose.addEventListener('click', closeDrawer);
    }

    // Close on overlay click
    if (brandDrawerOverlay) {
      brandDrawerOverlay.addEventListener('click', closeDrawer);
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && brandDrawer.classList.contains('active')) {
        closeDrawer();
      }
    });

    // Search functionality
    if (brandSearchInput) {
      brandSearchInput.addEventListener('input', (e) => {
        filterBrands();
      });
    }

    // Store methods for external access
    this.toggleBrandDrawer = () => {
      if (brandDrawer.classList.contains('active')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    };
  }

  /* ============================================================
     a11y: desktop mega menus + Shop by Vehicle (YMM) panel by keyboard.
     The panels open on :hover in CSS. Keyboard focus landing on a top-level
     trigger (or anywhere inside its panel) adds .sm-kb-open to the .nav-item,
     which sm-header.css shows exactly like :hover. Escape closes the panel and
     leaves focus on the trigger; Tab then moves on to the next top-level item.
     ArrowDown on a trigger opens the panel and focuses its first control.
     aria-expanded on each trigger follows hover/focus state.
     Mouse behaviour is unchanged (only :focus-visible focus opens a panel).
     ============================================================ */
  setupDesktopMenuKeyboard() {
    const isDesktop = () => window.innerWidth > 768;
    const items = document.querySelectorAll('.header-navigation .nav-item');

    const firstControlIn = (panel) => panel.querySelector(
      'select:not([disabled]), input:not([type="hidden"]):not([disabled]), button:not([disabled]), a[href]'
    );

    items.forEach((navItem, index) => {
      const trigger = navItem.querySelector(':scope > .mega-menu-trigger, :scope > .ymm-mega-menu-trigger, :scope > .brand-mega-menu-trigger');
      const panel = navItem.querySelector(':scope > .mega-menu, :scope > .ymm-mega-menu, :scope > .brand-mega-menu');
      if (!trigger || !panel) return;

      if (!panel.id) panel.id = 'sm-nav-panel-' + (index + 1);
      trigger.setAttribute('aria-controls', panel.id);
      if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');

      const positionPanel = () => {
        const nav = document.querySelector('.header-navigation');
        if (nav) panel.style.top = nav.getBoundingClientRect().bottom + 'px';
      };

      const open = () => {
        if (!isDesktop()) return;
        positionPanel();
        navItem.classList.remove('sm-kb-dismissed');
        navItem.classList.add('sm-kb-open');
        trigger.setAttribute('aria-expanded', 'true');
      };

      const close = () => {
        navItem.classList.remove('sm-kb-open');
        if (isDesktop()) trigger.setAttribute('aria-expanded', 'false');
      };

      navItem.addEventListener('focusin', (e) => {
        if (!isDesktop()) return;
        if (navItem.classList.contains('sm-kb-dismissed') && e.target === trigger) return;
        let keyboard = true;
        try { keyboard = e.target.matches(':focus-visible'); } catch (err) { keyboard = true; }
        if (keyboard || panel.contains(e.target)) open();
      });

      navItem.addEventListener('focusout', (e) => {
        if (e.relatedTarget && navItem.contains(e.relatedTarget)) return;
        // Native <select> pickers can blur with no relatedTarget while still open
        if (!e.relatedTarget && panel.contains(document.activeElement)) return;
        navItem.classList.remove('sm-kb-dismissed');
        close();
      });

      navItem.addEventListener('mouseenter', () => {
        if (isDesktop() && !navItem.classList.contains('sm-kb-dismissed')) trigger.setAttribute('aria-expanded', 'true');
      });
      navItem.addEventListener('mouseleave', () => {
        if (isDesktop() && !navItem.classList.contains('sm-kb-open')) trigger.setAttribute('aria-expanded', 'false');
      });

      navItem.addEventListener('keydown', (e) => {
        if (!isDesktop()) return;
        if (e.key === 'Escape') {
          e.stopPropagation();
          close();
          navItem.classList.add('sm-kb-dismissed');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus({ preventScroll: true });
        } else if (e.key === 'ArrowDown' && e.target === trigger) {
          e.preventDefault();
          open();
          const first = firstControlIn(panel);
          if (first) window.requestAnimationFrame(() => first.focus());
        }
      });
    });

    // The Shop by Vehicle trigger is an <a role="button" href="javascript:void(0)">:
    // Enter already fires click; Space must activate it too (native button behaviour).
    document.querySelectorAll('.ymm-mega-menu-trigger').forEach((t) => {
      t.addEventListener('keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Spacebar') return;
        e.preventDefault();
        if (!isDesktop()) {
          t.click();
          return;
        }
        const navItem = t.closest('.nav-item');
        const panel = navItem && navItem.querySelector('.ymm-mega-menu');
        if (!navItem || !panel) return;
        navItem.classList.remove('sm-kb-dismissed');
        navItem.classList.add('sm-kb-open');
        t.setAttribute('aria-expanded', 'true');
        const first = firstControlIn(panel);
        if (first) window.requestAnimationFrame(() => first.focus());
      });
    });
  }

  setupTertiaryVehicleForm() {
    // Tertiary vehicle form is now handled by snippets/vehicle-filter-popup.liquid
    // which directly targets #tertiary-year, #tertiary-make, etc.
    // This prevents race conditions and duplicate logic.
    return;
  }
}


// Scroll to Brands section
function scrollToBrands(event) {
  event.preventDefault();
  const brandsSection = document.getElementById('exclusive-brands');
  if (brandsSection) {
    // Close mobile menu if open
    const mobileMenu = document.getElementById('nav-menu');
    if (mobileMenu && mobileMenu.classList.contains('active')) {
      mobileMenu.classList.remove('active');
      document.body.style.overflow = '';
    }
    // Smooth scroll to section with offset for sticky header
    const headerHeight = 120;
    const elementPosition = brandsSection.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
}

function handleBrandMegaSelect() {
  const brandSelect = document.getElementById('brandMegaSelect');
  const selectedValue = brandSelect.value;

  if (selectedValue) {
    window.location.href = selectedValue;
  }
}

window.ModernHeader = ModernHeader;
window.scrollToBrands = scrollToBrands;
window.handleBrandMegaSelect = handleBrandMegaSelect;
})();